import { useCallback, useEffect, useRef, useState } from "react";

export type HeadingStatus = "idle" | "unsupported" | "denied" | "active";

interface UseDeviceHeading {
  heading: number | null;
  status: HeadingStatus;
  enable: () => Promise<boolean>;
  disable: () => void;
}

function getScreenAngle(): number {
  if (typeof window === "undefined") return 0;
  const so = window.screen?.orientation;
  if (so && typeof so.angle === "number") return so.angle || 0;
  const legacy = (window as any).orientation;
  return typeof legacy === "number" ? legacy : 0;
}

// Subscribe to the device compass, expose a smoothed heading in degrees
// clockwise from true north. Returns an unbounded (non-wrapping) value so
// CSS / map rotation can interpolate smoothly across the 360->0 boundary.
export function useDeviceHeading(): UseDeviceHeading {
  const [heading, setHeading] = useState<number | null>(null);
  const [status, setStatus] = useState<HeadingStatus>("idle");

  const smoothedRef = useRef<number | null>(null);
  const lastEmittedRef = useRef<number | null>(null);
  const lastEmitTimeRef = useRef<number>(0);
  const subscribedRef = useRef(false);
  const requestInFlightRef = useRef(false);

  const handle = useCallback((event: DeviceOrientationEvent) => {
    let raw: number | null = null;
    const compass = (event as any).webkitCompassHeading;
    if (typeof compass === "number" && !Number.isNaN(compass)) {
      // iOS: already degrees clockwise from north.
      raw = compass;
    } else if (event.absolute === true && typeof event.alpha === "number") {
      // Android absolute orientation: alpha is counter-clockwise from north.
      raw = 360 - event.alpha + getScreenAngle();
    }
    if (raw === null || Number.isNaN(raw)) return;
    raw = ((raw % 360) + 360) % 360;

    // Circular low-pass filter on an unbounded accumulator.
    const prev = smoothedRef.current;
    let next: number;
    if (prev === null) {
      next = raw;
    } else {
      const prevMod = ((prev % 360) + 360) % 360;
      const diff = ((raw - prevMod + 540) % 360) - 180;
      next = prev + diff * 0.18;
    }
    smoothedRef.current = next;

    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    if (now - lastEmitTimeRef.current < 120) return;
    const last = lastEmittedRef.current;
    if (last !== null && Math.abs(next - last) < 1) return;
    lastEmitTimeRef.current = now;
    lastEmittedRef.current = next;
    setHeading(next);
  }, []);

  const subscribe = useCallback(() => {
    if (subscribedRef.current) return;
    subscribedRef.current = true;
    window.addEventListener("deviceorientationabsolute", handle as EventListener, true);
    window.addEventListener("deviceorientation", handle as EventListener, true);
  }, [handle]);

  const unsubscribe = useCallback(() => {
    subscribedRef.current = false;
    window.removeEventListener("deviceorientationabsolute", handle as EventListener, true);
    window.removeEventListener("deviceorientation", handle as EventListener, true);
  }, [handle]);

  const enable = useCallback(async () => {
    if (typeof window === "undefined" || !("DeviceOrientationEvent" in window)) {
      setStatus("unsupported");
      return false;
    }
    // Already listening — don't re-trigger the iOS permission request.
    if (subscribedRef.current) {
      setStatus("active");
      return true;
    }
    // A single tap can fire both the global gesture handler and the hint
    // button, so guard against overlapping requestPermission() calls.
    if (requestInFlightRef.current) return false;
    const req = (DeviceOrientationEvent as any).requestPermission;
    if (typeof req === "function") {
      requestInFlightRef.current = true;
      try {
        const res = await req.call(DeviceOrientationEvent);
        if (res !== "granted") {
          setStatus("denied");
          return false;
        }
      } catch {
        setStatus("denied");
        return false;
      } finally {
        requestInFlightRef.current = false;
      }
    }
    subscribe();
    setStatus("active");
    return true;
  }, [subscribe]);

  const disable = useCallback(() => {
    unsubscribe();
    smoothedRef.current = null;
    lastEmittedRef.current = null;
    setHeading(null);
    setStatus((prev) => (prev === "active" ? "idle" : prev));
  }, [unsubscribe]);

  useEffect(() => () => unsubscribe(), [unsubscribe]);

  return { heading, status, enable, disable };
}
